/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useRef} from 'react';

/**
 * useEventListener is a custom hook which handles addition and removal of  
 * the event listeners dynamically in the web page.
 * 
 * eventName : It can be any event like scroll, mouseDown, click event etc.
 * eventHandler : it is the function we need to execute when a particular event is triggered.
 * element : an html element with reference to it's 'id' where we check for the event to occur. 
 * 
 * useEventListener('scroll', onHandleScrollEvent, htmlElement);
 */
export const useEventListener = (
  eventName: string,
  eventHandler: Function,
  element = globalThis
) => {
  const handler = useRef(eventHandler);

  useEffect(() => {
    handler.current = eventHandler;
  }, [eventHandler]);

  useEffect(() => {
    const isSupported = element?.addEventListener;
    if (isSupported) {
      const eventListener = (event) => handler.current?.(event);
      element.addEventListener(eventName, eventListener);
      return () => {
        element.removeEventListener(eventName, eventListener);
      };
    }
  }, [eventName, element]);
};

export default useEventListener;
